import React, { RefObject, useEffect, useRef, useState } from 'react';
import { Slider as DXSlider } from 'devextreme-react/slider';
import '../../scss/controls/_slider.scss';

interface SliderProps {
    // The current value (should be greater than reserved)
    value: number;
    // The overall maximum value
    maxAmount: number;
    // The reserved value.
    // For example, if reserved = 20, then the reserved area ranges from min to 20.
    // The interactive slider then only handles values from 20 to max.
    reservedAmount: number;
    onValueChanged: (value: number) => void;
    noValueMessage?: string;
}

export const Slider: React.FC<SliderProps> = ({ value, maxAmount, reservedAmount, onValueChanged, noValueMessage }) => {
    const reservedPercentage = (reservedAmount / maxAmount) * 100;
    // const [interactiveValue, set_interactiveValue] = useState(reserved);
    const reservedSliderRef = useRef<HTMLDivElement>(null);
    const interactiveSliderRef = useRef<HTMLDivElement>(null);

    // Get the dynamic widths from the custom hook.
    const reservedSliderWidth = useContainerWidth(reservedSliderRef);
    const interactiveSliderWidth = useContainerWidth(interactiveSliderRef);

    // Accounting for the necessary padding within each container
    const effectiveWidth = reservedSliderWidth + interactiveSliderWidth - 80;

    // Here we take the reserved and interactive pixel values of the effective slider width,
    // and add a fixed 40px to account for the padding on both sides of the .dx-slider-wrapper.
    // We then convert that value back into a percentage relative to the effective width.
    // This allows us to adjust our slider lengths dynamically while maintaining the correct
    // visual ratios despite the fixed padding.
    const reservedPx = (reservedPercentage / 100) * effectiveWidth + 40;
    const interactivePx = ((100 - reservedPercentage) / 100) * effectiveWidth + 40;
    const reservedPercentAdj = effectiveWidth === 0 ? 100 : (reservedPx * 100) / effectiveWidth;
    const interactivePercentAdj = effectiveWidth === 0 ? 100 : (interactivePx * 100) / effectiveWidth;

    // For the reserved part, we display a static slider whose value is always at the reserved limit.
    const reservedValue = reservedPercentage;

    // For the interactive slider, value is always greater than or equal to reserved.
    const percentValue = ((value + reservedAmount) / maxAmount) * 100;
    return (
        <>
            <div className={`combined-slider-container ${!isNaN(reservedValue) ? '' : 'hidden'}`}>
                <div
                    className='slider-reserved-container'
                    style={{ flexBasis: `${reservedPercentAdj}%` }}
                    ref={reservedSliderRef}
                >
                    {/* Reserved area: disabled slider with hidden handle */}
                    <DXSlider
                        min={0}
                        max={reservedPercentage}
                        value={reservedValue}
                        disabled={true}
                        showRange={true}
                        tooltip={{ enabled: false }}
                    />
                </div>
                <div
                    className='slider-interactive-container'
                    style={{ flexBasis: `${interactivePercentAdj}%` }}
                    ref={interactiveSliderRef}
                >
                    <DXSlider
                        min={reservedPercentage}
                        max={100}
                        value={percentValue}
                        onValueChanged={(e) => {
                            const totalPercentSelected = e.value;
                            const additionalPercentageWithoutReserved = totalPercentSelected - reservedPercentage;
                            const amountSelected = (additionalPercentageWithoutReserved / 100) * maxAmount;

                            onValueChanged(amountSelected);
                        }}
                        tooltip={{
                            enabled: true,
                            showMode: 'onHover',
                            position: 'top',
                            format: (value: number) => `${Math.round(value)}%`,
                        }}
                        showRange={true}
                    />
                </div>
            </div>
            <div className='combined-slider-footer'>
                <div className='slider-reserved-amount'>
                    {!isNaN(reservedValue) ? (
                        `${Math.round(reservedValue)}% USED`
                    ) : (
                        <div style={{ paddingTop: '5px' }}>{noValueMessage}</div>
                    )}
                </div>
            </div>
        </>
    );
};

// This custom hook will update the width state whenever the observed element’s size changes.
export function useContainerWidth(ref: RefObject<HTMLElement>): number {
    const [width, setWidth] = useState(0);

    useEffect(() => {
        if (!ref.current) return;

        const updateWidth = (entries: ResizeObserverEntry[]) => {
            if (entries.length > 0) {
                setWidth(entries[0].contentRect.width);
            }
        };

        const observer = new ResizeObserver(updateWidth);
        observer.observe(ref.current);

        return () => {
            observer.disconnect();
        };
    }, [ref]);

    return width;
}
